/**
 * Admin/BoardConnector.tsx
 *
 * Manages the connection between ALL monday.com workspace boards and the
 * Chanelle OS Task Feed aggregation system.
 *
 * Features:
 *   - Discover all boards in the workspace
 *   - Toggle which boards feed into the aggregator (Include Tasks / Mentions)
 *   - Register/unregister boards in the Data Sources board (5092854138)
 *   - Trigger a manual sync
 *   - View sync history from the Aggregator board (5092855497)
 */

import { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '@core/store/appStore';
import { queryAllBoards } from '@core/api/queries';
import {
  queryDataSources,
  runAggregation,
  CHANELLE_USER_ID,
  TASK_FEED_BOARD_ID,
  DATA_SOURCES_BOARD_ID,
  AGGREGATOR_BOARD_ID,
} from '@core/intelligence/feedAggregator';
import { registerDataSource } from '@core/utils/feedBoardSetup';
import type { MondayBoard, DataSourceEntry, AggregationResult } from '@core/types';

// Board IDs to exclude from data source registration (infrastructure boards)
const EXCLUDED_BOARD_IDS = new Set([
  TASK_FEED_BOARD_ID,
  DATA_SOURCES_BOARD_ID,
  AGGREGATOR_BOARD_ID,
]);

interface WorkspaceBoard extends MondayBoard {
  isRegistered: boolean;
  dataSourceId?: string;
  includeTasks: boolean;
  includeMentions: boolean;
}

export function BoardConnector() {
  const { context, boardIds } = useAppStore();
  const userId = context?.user?.id?.toString() ?? CHANELLE_USER_ID;
  const userName = (context?.user as Record<string, unknown>)?.name as string ?? 'Chanelle';

  const [boards, setBoards] = useState<WorkspaceBoard[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [registering, setRegistering] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<AggregationResult | null>(null);
  const [message, setMessage] = useState('');

  // ── Load all workspace boards + existing data sources ───────────────────
  const loadBoards = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const [allBoards, dataSources] = await Promise.all([
        queryAllBoards(200),
        queryDataSources(),
      ]);

      // Build a lookup of registered data sources by board ID
      const registeredMap = new Map<string, DataSourceEntry>();
      for (const ds of dataSources) {
        if (ds.boardId) registeredMap.set(ds.boardId, ds);
      }

      // Merge workspace boards with registration status
      const merged: WorkspaceBoard[] = allBoards
        .filter((b) => b.state !== 'deleted')
        .filter((b) => !EXCLUDED_BOARD_IDS.has(b.id))
        .map((b) => {
          const ds = registeredMap.get(b.id);
          return {
            ...b,
            isRegistered: !!ds,
            dataSourceId: ds?.id,
            includeTasks: ds?.includeTasks ?? false,
            includeMentions: ds?.includeMentions ?? false,
          };
        })
        .sort((a, b) => {
          // Registered boards first, then alphabetical
          if (a.isRegistered !== b.isRegistered) return a.isRegistered ? -1 : 1;
          return a.name.localeCompare(b.name);
        });

      setBoards(merged);
      setMessage(`Found ${allBoards.length} boards, ${dataSources.length} connected`);
    } catch (err) {
      setMessage(`Failed to load boards: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadBoards(); }, [loadBoards]);

  // ── Register a board as a data source ───────────────────────────────────
  async function handleRegister(board: WorkspaceBoard) {
    setRegistering(board.id);
    try {
      const id = await registerDataSource(
        board.name,
        board.id,
        '', // workspace (auto-detected)
        true,  // include tasks
        true,  // include mentions
      );
      if (id) {
        setBoards((prev) =>
          prev.map((b) =>
            b.id === board.id
              ? { ...b, isRegistered: true, dataSourceId: id, includeTasks: true, includeMentions: true }
              : b,
          ),
        );
        setMessage(`Connected: ${board.name}`);
      }
    } catch (err) {
      setMessage(`Failed to connect ${board.name}: ${(err as Error).message}`);
    } finally {
      setRegistering(null);
    }
  }

  // ── Register ALL unregistered boards at once ────────────────────────────
  async function handleConnectAll() {
    setRegistering('all');
    const unregistered = boards.filter((b) => !b.isRegistered);
    let connected = 0;
    for (const board of unregistered) {
      try {
        const id = await registerDataSource(board.name, board.id, '', true, true);
        if (id) {
          connected++;
          setBoards((prev) =>
            prev.map((b) =>
              b.id === board.id
                ? { ...b, isRegistered: true, dataSourceId: id, includeTasks: true, includeMentions: true }
                : b,
            ),
          );
        }
      } catch {
        // Continue with other boards
      }
    }
    setMessage(`Connected ${connected} new boards`);
    setRegistering(null);
  }

  // ── Trigger aggregation sync ────────────────────────────────────────────
  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    setMessage('Running aggregation sync…');
    try {
      const result = await runAggregation(userId, userName);
      setSyncResult(result);
      setMessage(
        `Sync complete: ${result.newRecordsWritten} new records from ${result.totalSourceBoards} boards`,
      );
    } catch (err) {
      setMessage(`Sync failed: ${(err as Error).message}`);
    } finally {
      setSyncing(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  const registeredCount = boards.filter((b) => b.isRegistered).length;
  const unregisteredCount = boards.filter((b) => !b.isRegistered).length;

  return (
    <div className="cos-admin-card">
      <h3 className="cos-admin-heading">Board Connector</h3>
      <p className="cos-admin-muted">
        Connect your monday.com boards to Chanelle OS. Connected boards feed tasks and
        mentions into your unified personal feed (My Work &amp; Inbox).
      </p>

      {/* Status banner */}
      {message && (
        <div className="cos-admin-banner cos-admin-banner--info">{message}</div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px', margin: '12px 0' }}>
        <button
          type="button"
          className="cos-btn cos-btn--primary cos-btn--sm"
          disabled={syncing || loading}
          onClick={handleSync}
        >
          {syncing ? 'Syncing…' : '🔄 Sync Now'}
        </button>
        <button
          type="button"
          className="cos-btn cos-btn--secondary cos-btn--sm"
          disabled={registering !== null || unregisteredCount === 0}
          onClick={handleConnectAll}
        >
          {registering === 'all' ? 'Connecting…' : `⚡ Connect All (${unregisteredCount})`}
        </button>
        <button
          type="button"
          className="cos-btn cos-btn--secondary cos-btn--sm"
          disabled={loading}
          onClick={() => void loadBoards()}
        >
          Refresh
        </button>
      </div>

      {/* Sync results */}
      {syncResult && (
        <div className="cos-admin-section" style={{ background: 'var(--color-bg-hover, #f5f6f8)', padding: '8px 12px', borderRadius: '6px', marginBottom: '12px' }}>
          <strong>Last sync:</strong>{' '}
          {syncResult.tasksFound} tasks, {syncResult.mentionsFound} mentions found.{' '}
          {syncResult.newRecordsWritten} written, {syncResult.duplicatesSkipped} duplicates skipped.
          {syncResult.errors.length > 0 && (
            <div style={{ color: 'var(--color-error, #d83a52)', marginTop: '4px' }}>
              {syncResult.errors.length} error(s): {syncResult.errors[0]}
            </div>
          )}
        </div>
      )}

      {/* Infrastructure boards */}
      <section className="cos-admin-section">
        <h4 className="cos-admin-subheading">Infrastructure</h4>
        <ul className="cos-admin-list">
          <li className="cos-admin-list-item cos-admin-list-item--success">
            Task Feed: {TASK_FEED_BOARD_ID}
          </li>
          <li className="cos-admin-list-item cos-admin-list-item--success">
            Data Sources: {DATA_SOURCES_BOARD_ID}
          </li>
          <li className="cos-admin-list-item cos-admin-list-item--success">
            Aggregator: {AGGREGATOR_BOARD_ID}
          </li>
        </ul>
      </section>

      {/* Board list */}
      <section className="cos-admin-section">
        <h4 className="cos-admin-subheading">
          Workspace Boards ({registeredCount} connected / {boards.length} total)
        </h4>

        {loading ? (
          <p className="cos-admin-muted">Scanning workspace…</p>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border, #e6e9ef)', textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>Board</th>
                  <th style={{ padding: '6px 8px', width: '80px' }}>ID</th>
                  <th style={{ padding: '6px 8px', width: '80px' }}>Type</th>
                  <th style={{ padding: '6px 8px', width: '100px' }}>Status</th>
                  <th style={{ padding: '6px 8px', width: '100px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {boards.map((board) => (
                  <tr
                    key={board.id}
                    style={{
                      borderBottom: '1px solid var(--color-border, #e6e9ef)',
                      background: board.isRegistered ? 'var(--color-bg-selected, #e8f4e8)' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '6px 8px', fontWeight: board.isRegistered ? 600 : 400 }}>
                      {board.isRegistered ? '🟢' : '⚪'} {board.name}
                    </td>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '11px' }}>
                      {board.id}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{
                        fontSize: '11px',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        background: 'var(--color-bg-hover, #f0f0f0)',
                      }}>
                        {board.board_kind ?? 'share'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {board.isRegistered ? (
                        <span className="cos-badge cos-badge--success" style={{ fontSize: '11px' }}>
                          Connected
                        </span>
                      ) : (
                        <span className="cos-badge cos-badge--neutral" style={{ fontSize: '11px' }}>
                          Not connected
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      {!board.isRegistered && (
                        <button
                          type="button"
                          className="cos-btn cos-btn--primary cos-btn--xs"
                          style={{ fontSize: '11px', padding: '2px 8px' }}
                          disabled={registering !== null}
                          onClick={() => void handleRegister(board)}
                        >
                          {registering === board.id ? '…' : 'Connect'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* COS Board IDs (from store) */}
      <section className="cos-admin-section">
        <h4 className="cos-admin-subheading">Chanelle OS Board IDs (from store)</h4>
        <ul className="cos-admin-list">
          {Object.entries(boardIds)
            .filter(([, v]) => v)
            .map(([key, value]) => (
              <li key={key} className="cos-admin-list-item cos-admin-list-item--success">
                {key}: {value}
              </li>
            ))}
        </ul>
      </section>
    </div>
  );
}
